import autobind from "autobind-decorator";
import delegate from "es6-delegate";
import { lazyValidate, ValidationError } from "../lazyValidate";
import ItemNumber from "./ItemNumber";
import Suffix from "./Suffix";

export default class Product {
  placeHolderImageSrc = "/images/products/imagecomingsoon.png";

  get primaryImageSrc() {
    return this.primaryImageSrcPrivate || this.placeHolderImageSrc;
  }

  set primaryImageSrc(imageSrc) {
    this.primaryImageSrcPrivate = imageSrc;
  }

  get mnbuy() {
    return this.form.querySelector("[name=mnbuy]") || { value: 1 };
  }

  get price() {
    return Number(this.form.querySelector("[name=price]").value);
  }

  get unavailable() {
    return this.itemNumber.selectedSuffixOption.dataset.unavailable === "Y";
  }

  get available() {
    return !this.unavailable;
  }

  get outletItem() {
    return this.form.dataset.outletItem;
  }

  get minimumQuantity() {
    const minBuy = Math.max(this.mnbuy.value, 1);
    return minBuy > this.divisor ? minBuy : this.divisor;
  }

  get mxbuy() {
    return this.form.querySelector("[name=mxbuy]") || {};
  }

  get divisor() {
    return Number(this.mxbuy.value || 1);
  }

  get warning() {
    return this.form.classList.contains("has-product-warning");
  }

  get quantity() {
    return Number(this.quantityInput.value || 1);
  }

  get quantityInput() {
    return this.form.querySelector("[name=qty]") || {};
  }

  get quantitySelect() {
    return this.form.querySelector("[data-component=specialQuantity] select");
  }

  get quantitySelectLastOption() {
    return this.quantitySelect.lastElementChild || {};
  }

  get otherQuantitySelected() {
    if (!this.quantitySelect) return false;
    return this.quantitySelect.value === this.quantitySelectLastOption.value;
  }

  get specialQuantity() {
    return this.minimumQuantity > 1 || this.divisor > 1;
  }

  /**
   * @param {HTMLFormElement} form
   */
  constructor(form) {
    this.form = form;
    if (!this.form) return;

    this.itemNumber = new ItemNumber(this.form);

    delegate(this.form, "change", "[name=qty]", this.quantityChange);
    this.form.addEventListener("ItemNumber.change", this.itemNumberChange);
  }

  @autobind
  itemNumberChange() {
    this.updateFromSuffixObject();
    this.dispatch("Product.itemNumber.change");
  }

  dispatch(eventName) {
    const event = new Event(eventName, { bubbles: true });
    event.detail = this;

    this.form.dispatchEvent(event);
  }

  updateFromSuffixObject() {
    const item = Suffix.priceLookup(this.itemNumber);

    if (!item) return;

    this.mnbuy.value = item.min_buy;
    this.mxbuy.value = item.must_buy;
  }

  @autobind
  quantityChange() {
    this.dispatch("Product.quantity.change");
  }

  @lazyValidate
  minQuantity() {
    if (this.quantity < this.minimumQuantity) {
      throw new ValidationError(
        `Please enter a quantity of more than ${this.minimumQuantity - 1}.`
      );
    }
  }

  @lazyValidate
  increment() {
    if (this.quantity % this.divisor)
      throw new ValidationError(
        `Please enter a quantity as a multiple of ${this.divisor}`
      );
  }

  @lazyValidate
  itemNumberValid() {
    return this.itemNumber.valid();
  }
}
